import { Component, effect, inject, signal } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { UniqueIdService } from '../../../services/builder/unique-id.service';
import { WidgetService } from '../../../services/builder/widget.service';
import { baseCustomPanelStructureComponent } from '../../new-custom-panel/baseCustomPanelStructure';
import {
  RadioButtonCustomization,
  SelectOption,
} from '../RadioButton/radiobutton-edit';

@Component({
  selector: 'lib-combobox-custom-panel',
  template: `
    <lib-base-cpanel
      [content]="content"
      [form]="customizationForm"
      header="Personalizzazione Radio Button"
      (closed)="updateWidget($event)"
    />
    <ng-template #content>
      @if (customizationForm) {
        <div [formGroup]="customizationForm" class="flex flex-col gap-2">
          <div class="flex w-full flex-col">
            <label for="desc">Descrizione</label>
            <textarea
              id="desc"
              pTextarea
              formControlName="description"
            ></textarea>
          </div>
          <div class="flex w-full flex-col gap-2">
            @let optionForm = this.optionForm();
            @if (optionForm) {
              <div class="flex w-full flex-col gap-2 rounded-md">
                <p-table
                  [value]="optionForm"
                  tableStyleClass="p-fluid"
                  [scrollable]="true"
                  scrollHeight="400px"
                >
                  <ng-template pTemplate="header">
                    <tr>
                      <th>Descrizione</th>
                      <th class="text-center">Valore uguale a descrizione</th>
                      <th>Valore</th>
                      <th>Peso</th>
                      <th>Tipo</th>
                      <th class="w-0">
                        <button mat-icon-button (click)="addOption()">
                          <mat-icon svgIcon="heroicons_solid:plus"></mat-icon>
                        </button>
                      </th>
                    </tr>
                  </ng-template>
                  <ng-template
                    pTemplate="body"
                    let-option
                    let-rowIndex="rowIndex"
                  >
                    <tr>
                      <td>
                        <input
                          pInputText
                          placeholder="Descrizione"
                          [formControl]="indexOption(rowIndex).controls.label"
                        />
                      </td>
                      <td class="text-center">
                        <p-checkbox
                          [binary]="true"
                          [formControl]="
                            indexOption(rowIndex).controls
                              .valueEqualsDescription
                          "
                        ></p-checkbox>
                      </td>
                      <td>
                        <input
                          pInputText
                          placeholder="Valore"
                          [formControl]="indexOption(rowIndex).controls.value"
                        />
                      </td>
                      <td>
                        <p-input-number
                          [autofocus]="false"
                          placeholder="Peso"
                          [formControl]="indexOption(rowIndex).controls.weight"
                          mode="decimal"
                          minFractionDigits="0"
                          maxFractionDigits="2"
                        />
                      </td>
                      <td>
                        <p-select
                          [autofocus]="false"
                          [options]="optionTypes"
                          optionLabel="label"
                          optionValue="value"
                          appendTo="body"
                          [formControl]="indexOption(rowIndex).controls.type"
                        />
                      </td>
                      <td>
                        <button
                          mat-icon-button
                          (click)="removeOption(rowIndex)"
                        >
                          <mat-icon svgIcon="heroicons_solid:minus"></mat-icon>
                        </button>
                      </td>
                    </tr>
                  </ng-template>
                </p-table>

                <small id="weightHelp">
                  Il peso di ciascuna opzione indica quanto quella scelta incide
                  sul punteggio totale della sottosezione.<br />
                  Un valore più alto attribuisce maggiore importanza all'opzione
                  selezionata rispetto alle altre.<br />
                  Imposta a 0 se non vuoi che la selezione di questa opzione
                  contribuisca al punteggio.
                </small>
              </div>
              <div class="p-fluid flex flex-col">
                <label for="data">Valore predefinito</label>
                <p-select
                  [autofocus]="false"
                  id="data"
                  formControlName="defaultValue"
                  [options]="customizationForm.controls.options.value"
                  optionLabel="label"
                  optionValue="value"
                  class="w-full"
                  appendTo="body"
                />
              </div>
            }
            <div class="flex w-full flex-row">
              <label for="required" class="w-full self-start">
                Campo obbligatorio
              </label>
              <p-checkbox
                id="required"
                formControlName="required"
                [binary]="true"
                class="self-end"
              ></p-checkbox>
            </div>
          </div>
        </div>
      }
    </ng-template>
  `,
  imports: [
    MatIconModule,
    SelectModule,
    TableModule,
    ReactiveFormsModule,
    baseCustomPanelStructureComponent,
    InputTextModule,
    InputNumberModule,
    MatButtonModule,
    CheckboxModule,
    FormsModule,
    TextareaModule,
  ],
})
export class ComboBoxCPanelComponent {
  private fb = inject(FormBuilder);
  protected widgetService = inject(WidgetService<RadioButtonCustomization>);
  protected uniqueIdService = inject(UniqueIdService);

  customizationForm = this.fb.group({
    id: [
      '',
      [
        Validators.required,
        this.uniqueIdService.forbiddenIdValidator(
          this.widgetService.uniqueId ?? '',
        ),
        this.uniqueIdService.isEmptySpace(),
      ],
    ],
    description: [''],
    options: this.fb.array<
      FormGroup<{
        label: FormControl<string | null>;
        value: FormControl<string | number | null>;
        valueEqualsDescription: FormControl<boolean | null>;
        type: FormControl<'string' | 'number' | null>;
        weight: FormControl<number | null>;
      }>
    >([]),
    defaultValue: [''],
    required: [false],
    showIfRule: ['', [this.uniqueIdService.idNotExistsValidator()]],
    autocompleteRule: ['', [this.uniqueIdService.idNotExistsValidator()]],
    // topology: [0],
  });

  optionForm = signal<SelectOption[]>([]);

  topologies = [
    {
      value: 0,
      viewValue: 'Orizzontale',
    },
    {
      value: 1,
      viewValue: 'Verticale',
    },
  ];

  optionTypes = [
    {
      label: 'string',
      value: 'string',
    },
    {
      label: 'number',
      value: 'number',
    },
  ];

  topologyStyle = 'flex-row gap-5';

  constructor() {
    effect(() => {
      const widget = this.widgetService.widget();
      if (!widget) return;

      const uniqueId = this.widgetService.uniqueId;
      if (!uniqueId) return;

      this.customizationForm.patchValue({
        id: uniqueId,
        description: widget.customization.description,
        defaultValue: widget.customization.defaultValue,
        required: widget.customization.required,
        showIfRule: widget.customization.showIfRule,
        autocompleteRule: widget.customization.autocompleteRule,
        // topology: widget.customization.topology,
      });

      widget.customization.options.forEach((option: SelectOption) => {
        this.addOption({
          label: option.label,
          value: option.value,
          valueEqualsDescription: option.valueEqualsDescription,
          type: typeof option.value === 'number' ? 'number' : 'string',
          weight: option.weight,
        });
      });

      this.optionForm.set(widget.customization.options);
    });
  }

  addOption(config: Partial<SelectOption> = {}) {
    const newOption = this.fb.group({
      label: this.fb.control<string | null>(
        config.label ??
          `Opzione ${this.customizationForm.controls.options.length + 1}`,
      ),
      value: this.fb.control<string | number | null>(
        config.value ??
          `option_${this.customizationForm.controls.options.length + 1}`,
      ),
      valueEqualsDescription: this.fb.control<boolean | null>(
        config.valueEqualsDescription ?? false,
      ),
      type: this.fb.control<'string' | 'number' | null>(
        config.type ?? 'string',
      ),
      weight: this.fb.control<number | null>(config.weight ?? 0),
    });

    newOption.controls.label.valueChanges.subscribe((val) => {
      if (newOption.controls.valueEqualsDescription.value) {
        newOption.controls.value.setValue(val);
      }
    });

    newOption.controls.type.valueChanges.subscribe((val) => {
      if (val === 'string') {
        newOption.controls.value.setValue(
          newOption.controls.value.value?.toString() ?? '',
        );
      } else {
        const tmp = parseFloat(
          newOption.controls.value.value?.toString() ?? '',
        );
        if (!isNaN(tmp)) {
          newOption.controls.value.setValue(tmp);
        } else {
          newOption.controls.type.setValue('string', { emitEvent: false });
        }
      }
    });

    newOption.controls.value.valueChanges.subscribe((val) => {
      if (!val) return;
      const tmp = parseFloat(val.toString() ?? '');
      if (isNaN(tmp)) {
        newOption.controls.type.setValue('string', { emitEvent: false });
      }
      if (val != newOption.controls.label.value) {
        newOption.controls.valueEqualsDescription.setValue(false, {
          emitEvent: false,
        });
      }
    });

    newOption.controls.valueEqualsDescription.valueChanges.subscribe((val) => {
      if (val) {
        newOption.controls.value.setValue(newOption.controls.label.value, {
          emitEvent: false,
        });
      }
    });

    this.customizationForm.controls.options.push(newOption);

    this.optionForm.set(
      this.customizationForm.controls.options.value.filter(
        (opt): opt is SelectOption =>
          opt.label != null && opt.value != null && opt.type != null,
      ),
    );
  }

  indexOption(int: number) {
    return this.customizationForm.controls.options.at(int) as FormGroup<{
      label: FormControl<string | null>;
      value: FormControl<string | null>;
      valueEqualsDescription: FormControl<boolean | null>;
      type: FormControl<'string' | 'number' | null>;
      weight: FormControl<number | null>;
    }>;
  }

  removeOption(index: number) {
    this.customizationForm.controls.options.removeAt(index);
    this.optionForm.set(
      this.customizationForm.controls.options.value.filter(
        (opt): opt is SelectOption =>
          opt.label != null && opt.value != null && opt.type != null,
      ),
    );
  }

  updateWidget(closed: boolean) {
    if (!closed) {
      this.widgetService.settingVisibility.set(false);
      return;
    }

    const formValue = this.customizationForm.value;
    const id = formValue.id?.trim();
    if (!id) return;

    this.widgetService.updateWidget(id, {
      description: formValue.description,
      options: formValue.options,
      defaultValue: formValue.defaultValue,
      required: formValue.required,
      showIfRule: formValue.showIfRule,
      autocompleteRule: formValue.autocompleteRule,
      // topology: formValue.topology,
    });
    this.widgetService.settingVisibility.set(false);
  }
}
